import { useState, useEffect, useCallback, useMemo } from 'react'
import {
    Activity,
    Bot,
    Phone,
    PhoneCall,
    RefreshCw,
    AlertCircle,
    Clock,
    Mic,
    Users,
    Server,
    ChevronDown,
    ChevronUp,
    ChevronRight,
    Search,
    Play,
    Square,
    RotateCw,
    Power,
    Check,
    X,
    Eye,
    EyeOff,
    ArrowUp,
    ArrowDown,
    Minus,
    AlertTriangle,
    CheckCircle,
    VolumeX,
    PhoneOff,
    Ban,
    UserX,
    Voicemail,
    ArrowRightLeft,
    FileText,
    PhoneOutgoing,
    PhoneForwarded,
    TrendingUp
} from 'lucide-react'
import { monitoringApi, dialerApi } from '../services/api'
import SearchableDropdown from '../components/SearchableDropdown'
import './MonitoringV2.css'

// ============================================
// CONSTANTS & HELPERS
// ============================================
const STALE_THRESHOLD_MINUTES = 2
const REFRESH_OPTIONS = [
    { value: 10000, label: '10s' },
    { value: 30000, label: '30s' },
    { value: 60000, label: '60s' }
]

// Status priority for sorting (lower = shown first)
const STATUS_PRIORITY = {
    error: 1,
    waiting_approval: 2,
    offline: 3,
    in_call: 4,
    online: 5,
    idle: 6
}

// Disposition icons and labels
const DISPOSITION_CONFIG = {
    'transfer-call': { icon: ArrowRightLeft, label: 'X', title: 'Transfers', color: '#10b981' },
    'answering-machine': { icon: Voicemail, label: 'AM', title: 'Answering Machine', color: '#6b7280' },
    'not-interested': { icon: UserX, label: 'NI', title: 'Not Interested', color: '#f59e0b' },
    'no-answer': { icon: PhoneOff, label: 'NA', title: 'No Answer', color: '#6b7280' },
    'do-not-call': { icon: Ban, label: 'DNC', title: 'Do Not Call', color: '#ef4444' },
    'age-do-not-qualify': { icon: UserX, label: 'DNQ', title: 'Age DNQ', color: '#f97316' }
}

// Health thresholds
const getHealthClass = (value) => {
    if (value < 70) return 'good'
    if (value < 85) return 'warning'
    return 'critical'
}

// Format time ago
const timeAgo = (date) => {
    if (!date) return 'Never'
    const seconds = Math.abs(Math.floor((new Date() - new Date(date)) / 1000))
    if (seconds < 60) return 'Just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    return `${Math.floor(seconds / 86400)}d ago`
}

// Check if bot is stale
const isStale = (lastHeartbeat) => {
    if (!lastHeartbeat) return true
    const seconds = Math.floor((new Date() - new Date(lastHeartbeat)) / 1000)
    return seconds > STALE_THRESHOLD_MINUTES * 60
}

// Get effective status
const getEffectiveStatus = (bot) => {
    if (bot.start_allowed === 0) return 'waiting_approval'
    if (isStale(bot.last_heartbeat)) return 'offline'
    if (bot.operational_state === 'error' || bot.status === 'error') return 'error'
    if (bot.operational_state === 'in_call' || bot.status === 'in_call') return 'in_call'
    if (bot.status === 'online' || bot.status === 'idle') return 'online'
    return 'offline'
}

// Format number with commas
const formatNumber = (num) => {
    if (num === undefined || num === null) return '0'
    return num.toLocaleString()
}

// Calculate transfer rate
const calcRate = (calls, xfers) => {
    if (!calls || calls === 0) return '0.0'
    return ((xfers / calls) * 100).toFixed(1)
}

// ============================================
// MAIN COMPONENT
// ============================================
export default function MonitoringV2() {
    // State
    const [summary, setSummary] = useState(null)
    const [bots, setBots] = useState([])
    const [dialerStats, setDialerStats] = useState([])
    const [voiceSetStats, setVoiceSetStats] = useState([])
    const [avatarStats, setAvatarStats] = useState([])
    const [serverStats, setServerStats] = useState([])
    const [errors, setErrors] = useState([])
    const [dialers, setDialers] = useState([])

    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState('all-bots')
    const [refreshInterval, setRefreshInterval] = useState(30000)
    const [lastRefresh, setLastRefresh] = useState(new Date())

    // Filters
    const [statusFilter, setStatusFilter] = useState('all')
    const [dialerFilter, setDialerFilter] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [excludedBots, setExcludedBots] = useState(() => {
        const saved = localStorage.getItem('excludedBots')
        return saved ? JSON.parse(saved) : []
    })

    // UI State
    // Theater mode removed - wasn't providing value
    const [alertsCollapsed, setAlertsCollapsed] = useState(false)
    const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' })
    const [selectedBots, setSelectedBots] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [actionLoading, setActionLoading] = useState({})
    const [expandedError, setExpandedError] = useState(null)
    const [vmConfirm, setVmConfirm] = useState(null)

    const BOTS_PER_PAGE = 50

    // Save excluded bots to localStorage
    useEffect(() => {
        localStorage.setItem('excludedBots', JSON.stringify(excludedBots))
    }, [excludedBots])

    // Load reference data
    useEffect(() => {
        const loadReferenceData = async () => {
            try {
                const dialerRes = await dialerApi.getAll()
                setDialers(dialerRes.data || [])
            } catch (err) {
                console.error('Failed to load reference data:', err)
            }
        }
        loadReferenceData()
    }, [])

    // Main data fetch
    const fetchData = useCallback(async (showRefreshing = false) => {
        try {
            if (showRefreshing) setRefreshing(true)
            setError(null)

            // Build date params for today
            const now = new Date()
            const localDate = now.getFullYear() + '-' +
                String(now.getMonth() + 1).padStart(2, '0') + '-' +
                String(now.getDate()).padStart(2, '0')
            const dateParams = { start_date: localDate, end_date: localDate }

            const [summaryRes, botsRes, dialerStatsRes, voiceSetStatsRes, avatarStatsRes, serverStatsRes, errorsRes] =
                await Promise.allSettled([
                    monitoringApi.getSummary(),
                    monitoringApi.getAllStatus(),
                    monitoringApi.getStatsByDialer(dateParams),
                    monitoringApi.getStatsByVoiceSet(dateParams),
                    monitoringApi.getStatsByAvatar(dateParams),
                    monitoringApi.getServersStats(),
                    monitoringApi.getErrors({ limit: 100 })
                ])

            if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data)
            if (botsRes.status === 'fulfilled') {
                const botsWithStatus = (botsRes.value.data || []).map(bot => ({
                    ...bot,
                    effectiveStatus: getEffectiveStatus(bot),
                    isStale: isStale(bot.last_heartbeat)
                }))
                setBots(botsWithStatus)
            }
            if (dialerStatsRes.status === 'fulfilled') setDialerStats(dialerStatsRes.value.data?.dialers || [])
            if (voiceSetStatsRes.status === 'fulfilled') setVoiceSetStats(voiceSetStatsRes.value.data?.voice_sets || [])
            if (avatarStatsRes.status === 'fulfilled') setAvatarStats(avatarStatsRes.value.data?.avatars || [])
            if (serverStatsRes.status === 'fulfilled') setServerStats(serverStatsRes.value.data?.servers || [])
            if (errorsRes.status === 'fulfilled') setErrors(errorsRes.value.data?.data?.errors || [])

            setLastRefresh(new Date())
        } catch (err) {
            console.error('Monitoring fetch error:', err)
            setError(err.message)
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [])

    // Initial load and auto-refresh
    useEffect(() => {
        fetchData()
    }, [fetchData])

    useEffect(() => {
        if (refreshInterval === 0) return
        const interval = setInterval(() => fetchData(false), refreshInterval)
        return () => clearInterval(interval)
    }, [refreshInterval, fetchData])

    // Filter and sort bots
    const filteredBots = useMemo(() => {
        let result = bots.filter(bot => !excludedBots.includes(bot.sip_id))

        // Status filter
        if (statusFilter !== 'all') {
            result = result.filter(bot => bot.effectiveStatus === statusFilter)
        }

        // Dialer filter
        if (dialerFilter) {
            result = result.filter(bot => bot.dialer_id === parseInt(dialerFilter))
        }

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase()
            result = result.filter(bot =>
                bot.sip_id?.toLowerCase().includes(query) ||
                bot.dialer_name?.toLowerCase().includes(query) ||
                bot.avatar_name?.toLowerCase().includes(query)
            )
        }

        // Sort
        result.sort((a, b) => {
            if (sortConfig.key === 'status') {
                const priorityA = STATUS_PRIORITY[a.effectiveStatus] || 99
                const priorityB = STATUS_PRIORITY[b.effectiveStatus] || 99
                return sortConfig.direction === 'asc' ? priorityA - priorityB : priorityB - priorityA
            }

            let valA = a[sortConfig.key]
            let valB = b[sortConfig.key]

            if (typeof valA === 'string') valA = valA?.toLowerCase() || ''
            if (typeof valB === 'string') valB = valB?.toLowerCase() || ''

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1
            return 0
        })

        return result
    }, [bots, excludedBots, statusFilter, dialerFilter, searchQuery, sortConfig])

    // Pagination
    const totalPages = Math.ceil(filteredBots.length / BOTS_PER_PAGE)
    const paginatedBots = filteredBots.slice(
        (currentPage - 1) * BOTS_PER_PAGE,
        currentPage * BOTS_PER_PAGE
    )

    // Calculate stats
    const botCounts = useMemo(() => {
        const counts = { error: 0, waiting: 0, offline: 0, in_call: 0, online: 0 }
        bots.filter(b => !excludedBots.includes(b.sip_id)).forEach(bot => {
            if (bot.effectiveStatus === 'error') counts.error++
            else if (bot.effectiveStatus === 'waiting_approval') counts.waiting++
            else if (bot.effectiveStatus === 'offline') counts.offline++
            else if (bot.effectiveStatus === 'in_call') counts.in_call++
            else counts.online++
        })
        return counts
    }, [bots, excludedBots])

    // Alerts
    const alerts = useMemo(() => {
        const result = []

        // Waiting approval
        if (botCounts.waiting > 0) {
            result.push({
                type: 'warning',
                text: `${botCounts.waiting} bots waiting for approval`,
                action: 'Approve All',
                onClick: () => handleBulkApprove()
            })
        }

        // Errors
        const unresolvedErrors = errors.filter(e => !e.resolved)
        if (unresolvedErrors.length > 0) {
            result.push({
                type: 'error',
                text: `${unresolvedErrors.length} unresolved errors`,
                action: 'View Errors',
                onClick: () => setActiveTab('errors')
            })
        }

        // Server warnings
        serverStats.forEach(server => {
            if (server.cpu_usage > 85 || server.ram_used_percent > 85) {
                result.push({
                    type: 'warning',
                    text: `Server ${server.name || server.ip} resources critical`,
                    detail: `CPU: ${server.cpu_usage?.toFixed(0)}% RAM: ${server.ram_used_percent?.toFixed(0)}%`
                })
            }
        })

        return result
    }, [botCounts, errors, serverStats])

    // Actions
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }))
    }

    const handleExcludeBot = (sipId) => {
        setExcludedBots(prev =>
            prev.includes(sipId)
                ? prev.filter(id => id !== sipId)
                : [...prev, sipId]
        )
    }

    const handleBotAction = async (sipId, action, bot) => {
        setActionLoading(prev => ({ ...prev, [sipId]: action }))
        try {
            if (action === 'start' || action === 'stop') {
                await monitoringApi.sendCommand([sipId], action)
            }
            setTimeout(() => fetchData(false), 2000)
        } catch (err) {
            console.error(`Failed to ${action} bot:`, err)
        } finally {
            setActionLoading(prev => ({ ...prev, [sipId]: null }))
        }
    }

    const handleVMRestart = async (bot) => {
        if (!bot.vmid || !bot.server_ip) return
        setActionLoading(prev => ({ ...prev, [bot.sip_id]: 'vm_restart' }))
        try {
            await monitoringApi.vmRestart(bot.server_ip, bot.vmid, bot.sip_id, false, bot.dialer_id)
            setTimeout(() => fetchData(false), 5000)
        } catch (err) {
            console.error('Failed to restart VM:', err)
        } finally {
            setActionLoading(prev => ({ ...prev, [bot.sip_id]: null }))
            setVmConfirm(null)
        }
    }

    const handleBulkApprove = async () => {
        const waitingBots = bots.filter(b => b.effectiveStatus === 'waiting_approval')
        for (const bot of waitingBots) {
            await monitoringApi.sendCommand([bot.sip_id], 'start')
        }
        fetchData(true)
    }

    const handleBulkAction = async (action) => {
        if (selectedBots.length === 0) return
        setActionLoading(prev => ({ ...prev, bulk: action }))
        try {
            await monitoringApi.sendCommand(selectedBots, action)
            setSelectedBots([])
            setTimeout(() => fetchData(false), 2000)
        } catch (err) {
            console.error(`Bulk ${action} failed:`, err)
        } finally {
            setActionLoading(prev => ({ ...prev, bulk: null }))
        }
    }

    if (loading && !summary) {
        return (
            <div className="monitoring-v2">
                <div className="loading-overlay">
                    <div className="loading-spinner"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="monitoring-v2">
            {/* Header */}
            <div className="monitoring-header">
                <div className="header-left">
                    <h1 className="monitoring-title">
                        Monitoring
                        <span className="live-badge">
                            <span className="live-dot"></span>
                            Live
                        </span>
                    </h1>
                </div>
                <div className="header-right">
                    <div className="refresh-timer">
                        <select
                            className="refresh-select"
                            value={refreshInterval}
                            onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                        >
                            {REFRESH_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        className="header-btn"
                        onClick={() => fetchData(true)}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                    </button>
                    <span className="current-time">{lastRefresh.toLocaleTimeString()}</span>
                </div>
            </div>

            {/* Error Banner */}
            {error && (
                <div className="alert-item" style={{ margin: '0 0 var(--space-md)' }}>
                    <div className="alert-content">
                        <AlertCircle size={16} className="alert-icon error" />
                        <span className="alert-text">Error: {error}</span>
                    </div>
                </div>
            )}

            {/* Server Health Strip */}
            <ServerHealthStrip servers={serverStats} />

            {/* Live Stats Bar */}
            <LiveStatsBar summary={summary} botCounts={botCounts} />

            {/* Alerts Panel */}
            {alerts.length > 0 && (
                <AlertsPanel
                    alerts={alerts}
                    collapsed={alertsCollapsed}
                    onToggle={() => setAlertsCollapsed(!alertsCollapsed)}
                />
            )}

            {/* Tabs */}
            <div className="monitoring-tabs">
                {[
                    { id: 'all-bots', icon: Bot, label: 'All Bots', count: bots.length - excludedBots.length },
                    { id: 'by-dialer', icon: Phone, label: 'By Dialer', count: dialerStats.length },
                    { id: 'by-voice-set', icon: Mic, label: 'Voice Sets', count: voiceSetStats.length },
                    { id: 'by-avatar', icon: Users, label: 'Avatars', count: avatarStats.length },
                    { id: 'errors', icon: AlertTriangle, label: 'Errors', count: errors.filter(e => !e.resolved).length },
                    { id: 'issues', icon: AlertCircle, label: 'Issues', count: botCounts.error + botCounts.waiting + botCounts.offline }
                ].map(tab => (
                    <button
                        key={tab.id}
                        className={`monitoring-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <tab.icon size={14} />
                        {tab.label}
                        {tab.count > 0 && <span className="tab-count">{tab.count}</span>}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'all-bots' && (
                    <AllBotsTab
                        bots={paginatedBots}
                        filteredCount={filteredBots.length}
                        totalBots={bots.length}
                        excludedCount={excludedBots.length}
                        dialers={dialers}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        dialerFilter={dialerFilter}
                        setDialerFilter={setDialerFilter}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        selectedBots={selectedBots}
                        setSelectedBots={setSelectedBots}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        totalPages={totalPages}
                        onExclude={handleExcludeBot}
                        onAction={handleBotAction}
                        onVMRestart={handleVMRestart}
                        onBulkAction={handleBulkAction}
                        actionLoading={actionLoading}
                        botCounts={botCounts}
                        vmConfirm={vmConfirm}
                        setVmConfirm={setVmConfirm}
                    />
                )}

                {activeTab === 'by-dialer' && (
                    <ByDialerTab stats={dialerStats} />
                )}

                {activeTab === 'by-voice-set' && (
                    <ByVoiceSetTab stats={voiceSetStats} />
                )}

                {activeTab === 'by-avatar' && (
                    <ByAvatarTab stats={avatarStats} />
                )}

                {activeTab === 'errors' && (
                    <ErrorsTab
                        errors={errors}
                        expandedError={expandedError}
                        setExpandedError={setExpandedError}
                        onRefresh={() => fetchData(true)}
                    />
                )}

                {activeTab === 'issues' && (
                    <IssuesTab
                        bots={bots}
                        excludedBots={excludedBots}
                        onApprove={handleBotAction}
                        onBulkApprove={handleBulkApprove}
                    />
                )}
            </div>

            {/* Excluded Bots Info */}
            {excludedBots.length > 0 && (
                <div style={{
                    padding: 'var(--space-xs) var(--space-md)',
                    background: 'var(--bg-elevated)',
                    borderTop: '1px solid var(--border-color)',
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <span>
                        <EyeOff size={12} style={{ marginRight: '6px' }} />
                        {excludedBots.length} bot{excludedBots.length > 1 ? 's' : ''} excluded from monitoring
                    </span>
                    <button
                        className="action-btn"
                        onClick={() => setExcludedBots([])}
                    >
                        Show All
                    </button>
                </div>
            )}
        </div>
    )
}

// ============================================
// SERVER HEALTH STRIP
// ============================================
function ServerHealthStrip({ servers }) {
    if (!servers || servers.length === 0) return null

    return (
        <div className="mon-servers-strip">
            {servers.map((server, idx) => {
                // Calculate percentages from GB values
                const cpuPct = server.cpu_usage || 0
                const ramPct = server.ram_total_gb > 0
                    ? ((server.ram_used_gb || 0) / server.ram_total_gb) * 100
                    : 0
                const diskPct = server.disk_total_gb > 0
                    ? ((server.disk_used_gb || 0) / server.disk_total_gb) * 100
                    : 0

                const cpuClass = getHealthClass(cpuPct)
                const ramClass = getHealthClass(ramPct)
                const diskClass = getHealthClass(diskPct)

                // Display name - show dialer name if available
                const displayName = server.name?.includes('(')
                    ? server.name.split('(')[1]?.replace(')', '')
                    : server.name || server.ip_address

                return (
                    <div key={idx} className="mon-server-card">
                        <div className="mon-server-header">
                            <Server size={14} className="mon-server-icon" />
                            <span className="mon-server-name">{displayName}</span>
                            <span className="mon-server-ip">{server.ip_address}</span>
                        </div>
                        <div className="mon-server-stats">
                            <div className="mon-stat-row">
                                <span className="mon-stat-label">CPU</span>
                                <div className="mon-stat-bar">
                                    <div className={`mon-stat-fill ${cpuClass}`} style={{ width: `${Math.min(cpuPct, 100)}%` }}></div>
                                </div>
                                <span className="mon-stat-value">{cpuPct.toFixed(0)}%</span>
                            </div>
                            <div className="mon-stat-row">
                                <span className="mon-stat-label">RAM</span>
                                <div className="mon-stat-bar">
                                    <div className={`mon-stat-fill ${ramClass}`} style={{ width: `${Math.min(ramPct, 100)}%` }}></div>
                                </div>
                                <span className="mon-stat-value">{ramPct.toFixed(0)}%</span>
                            </div>
                            <div className="mon-stat-row">
                                <span className="mon-stat-label">Disk</span>
                                <div className="mon-stat-bar">
                                    <div className={`mon-stat-fill ${diskClass}`} style={{ width: `${Math.min(diskPct, 100)}%` }}></div>
                                </div>
                                <span className="mon-stat-value">{diskPct.toFixed(0)}%</span>
                            </div>
                        </div>
                        <div className="mon-server-footer">
                            <span className="mon-vm-count">
                                <Activity size={12} />
                                {server.vm_running || 0} / {server.vm_count || 0} VMs
                            </span>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// ============================================
// LIVE STATS BAR
// ============================================
function LiveStatsBar({ summary, botCounts }) {
    const totalOnline = (botCounts.online || 0) + (botCounts.in_call || 0)
    const inCall = botCounts.in_call || 0
    const totalCalls = summary?.today?.total_calls || 0
    const transfers = summary?.today?.transfers || 0
    const rate = calcRate(totalCalls, transfers)

    return (
        <div className="stat-blocks">
            <div className="stat-block blue">
                <div className="stat-block-top">
                    <Bot size={18} />
                    <span className="stat-block-title">Bots Online</span>
                </div>
                <div className="stat-block-value">{formatNumber(totalOnline)}</div>
                <div className="stat-block-label">active now</div>
                <div className="stat-block-bar"><div className="stat-bar-fill" style={{ width: '100%' }}></div></div>
            </div>
            <div className="stat-block cyan">
                <div className="stat-block-top">
                    <Phone size={18} />
                    <span className="stat-block-title">In Call</span>
                </div>
                <div className="stat-block-value">{formatNumber(inCall)}</div>
                <div className="stat-block-label">right now</div>
                <div className="stat-block-bar"><div className="stat-bar-fill" style={{ width: inCall > 0 ? '100%' : '0%' }}></div></div>
            </div>
            <div className="stat-block purple">
                <div className="stat-block-top">
                    <PhoneOutgoing size={18} />
                    <span className="stat-block-title">Calls Today</span>
                </div>
                <div className="stat-block-value">{formatNumber(totalCalls)}</div>
                <div className="stat-block-label">total</div>
                <div className="stat-block-bar"><div className="stat-bar-fill" style={{ width: '100%' }}></div></div>
            </div>
            <div className="stat-block green">
                <div className="stat-block-top">
                    <PhoneForwarded size={18} />
                    <span className="stat-block-title">Transfers</span>
                </div>
                <div className="stat-block-value">{formatNumber(transfers)}</div>
                <div className="stat-block-label">today</div>
                <div className="stat-block-bar"><div className="stat-bar-fill" style={{ width: '100%' }}></div></div>
            </div>
            <div className="stat-block orange">
                <div className="stat-block-top">
                    <TrendingUp size={18} />
                    <span className="stat-block-title">Transfer Rate</span>
                </div>
                <div className="stat-block-value">{rate}<span className="stat-block-unit">%</span></div>
                <div className="stat-block-label">{parseFloat(rate) >= 5 ? 'excellent' : parseFloat(rate) >= 3 ? 'good' : 'needs work'}</div>
                <div className="stat-block-bar"><div className={`stat-bar-fill ${parseFloat(rate) >= 5 ? '' : parseFloat(rate) >= 3 ? 'warning' : 'critical'}`} style={{ width: `${Math.min(parseFloat(rate) * 10, 100)}%` }}></div></div>
            </div>
        </div>
    )
}

// ============================================
// ALERTS PANEL
// ============================================
function AlertsPanel({ alerts, collapsed, onToggle }) {
    return (
        <div className={`alerts-panel ${collapsed ? 'collapsed' : ''}`}>
            <div className="alerts-header" onClick={onToggle}>
                <div className="alerts-title">
                    <AlertTriangle size={14} />
                    Needs Attention
                    <span className="alerts-count">{alerts.length}</span>
                </div>
                <span className="alerts-toggle">
                    {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </span>
            </div>
            <div className="alerts-content">
                {alerts.map((alert, idx) => (
                    <div key={idx} className="alert-item">
                        <div className="alert-content">
                            <AlertCircle size={14} className={`alert-icon ${alert.type}`} />
                            <span className="alert-text">{alert.text}</span>
                            {alert.detail && <span className="alert-detail">{alert.detail}</span>}
                        </div>
                        {alert.action && (
                            <button className="alert-action primary" onClick={alert.onClick}>
                                {alert.action}
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

// ============================================
// ALL BOTS TAB
// ============================================
function AllBotsTab({
    bots, filteredCount, totalBots, excludedCount,
    dialers, statusFilter, setStatusFilter, dialerFilter, setDialerFilter,
    searchQuery, setSearchQuery, sortConfig, onSort,
    selectedBots, setSelectedBots, currentPage, setCurrentPage, totalPages,
    onExclude, onAction, onVMRestart, onBulkAction, actionLoading, botCounts,
    vmConfirm, setVmConfirm
}) {
    const toggleSelectAll = () => {
        if (selectedBots.length === bots.length) {
            setSelectedBots([])
        } else {
            setSelectedBots(bots.map(b => b.sip_id))
        }
    }

    const toggleSelect = (sipId) => {
        setSelectedBots(prev =>
            prev.includes(sipId)
                ? prev.filter(id => id !== sipId)
                : [...prev, sipId]
        )
    }

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <Minus size={10} style={{ opacity: 0.3 }} />
        return sortConfig.direction === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />
    }

    return (
        <>
            <div className="bots-tab-header">
                <div className="bots-filters">
                    <div className="search-wrapper">
                        <Search size={14} />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search SIP..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <SearchableDropdown
                        options={dialers}
                        value={dialerFilter}
                        onChange={setDialerFilter}
                        placeholder="Search dialers..."
                        allLabel="All Dialers"
                    />
                    <div className="filter-pills">
                        <button
                            className={`filter-pill ${statusFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('all')}
                        >
                            All
                        </button>
                        <button
                            className={`filter-pill ${statusFilter === 'error' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('error')}
                        >
                            <span className="pill-dot error"></span>
                            {botCounts.error}
                        </button>
                        <button
                            className={`filter-pill ${statusFilter === 'waiting_approval' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('waiting_approval')}
                        >
                            <span className="pill-dot waiting"></span>
                            {botCounts.waiting}
                        </button>
                        <button
                            className={`filter-pill ${statusFilter === 'offline' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('offline')}
                        >
                            <span className="pill-dot offline"></span>
                            {botCounts.offline}
                        </button>
                        <button
                            className={`filter-pill ${statusFilter === 'in_call' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('in_call')}
                        >
                            <span className="pill-dot in-call"></span>
                            {botCounts.in_call}
                        </button>
                        <button
                            className={`filter-pill ${statusFilter === 'online' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('online')}
                        >
                            <span className="pill-dot healthy"></span>
                            {botCounts.online}
                        </button>
                    </div>
                </div>
                <div className="bots-actions">
                    {selectedBots.length > 0 && (
                        <>
                            <span className="selected-info">{selectedBots.length} selected</span>
                            <button
                                className="action-btn start"
                                onClick={() => onBulkAction('start')}
                                disabled={actionLoading.bulk}
                            >
                                <Play size={12} /> Start
                            </button>
                            <button
                                className="action-btn stop"
                                onClick={() => onBulkAction('stop')}
                                disabled={actionLoading.bulk}
                            >
                                <Square size={12} /> Stop
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="bot-table-container">
                <table className="bot-table">
                    <thead>
                        <tr>
                            <th style={{ width: 30 }}>
                                <input
                                    type="checkbox"
                                    checked={selectedBots.length === bots.length && bots.length > 0}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <th style={{ width: 30 }} className={sortConfig.key === 'status' ? 'sorted' : ''} onClick={() => onSort('status')}>
                                {getSortIcon('status')}
                            </th>
                            <th className={sortConfig.key === 'sip_id' ? 'sorted' : ''} onClick={() => onSort('sip_id')}>
                                SIP {getSortIcon('sip_id')}
                            </th>
                            <th className={sortConfig.key === 'dialer_name' ? 'sorted' : ''} onClick={() => onSort('dialer_name')}>
                                Dialer {getSortIcon('dialer_name')}
                            </th>
                            <th>Avatar</th>
                            <th>Voice Set</th>
                            <th className={sortConfig.key === 'calls_today' ? 'sorted' : ''} onClick={() => onSort('calls_today')} style={{ textAlign: 'right' }}>
                                Calls {getSortIcon('calls_today')}
                            </th>
                            <th style={{ textAlign: 'right' }} title="Transfers">
                                <ArrowRightLeft size={12} />
                            </th>
                            <th style={{ textAlign: 'center' }} title="Answering Machine">AM</th>
                            <th style={{ textAlign: 'center' }} title="Not Interested">NI</th>
                            <th style={{ textAlign: 'center' }} title="Do Not Call">DNC</th>
                            <th>Last</th>
                            <th style={{ width: 140 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bots.map(bot => {
                            const isLoading = actionLoading[bot.sip_id]
                            const isVmConfirming = vmConfirm === bot.sip_id

                            return (
                                <tr key={bot.sip_id} className={selectedBots.includes(bot.sip_id) ? 'selected' : ''}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedBots.includes(bot.sip_id)}
                                            onChange={() => toggleSelect(bot.sip_id)}
                                        />
                                    </td>
                                    <td>
                                        <span className={`status-dot ${bot.effectiveStatus}`}></span>
                                    </td>
                                    <td className="bot-sip-id">{bot.sip_id}</td>
                                    <td className="bot-dialer">{bot.dialer_name || 'Unknown'}</td>
                                    <td className="bot-avatar" title={bot.avatar_name}>{bot.avatar_name || '-'}</td>
                                    <td className="bot-voice-set" title={bot.voice_set_name}>{bot.voice_set_name || 'Classic'}</td>
                                    <td className="bot-calls">{bot.calls_today || 0}</td>
                                    <td className="bot-xfers" style={{ color: '#10b981' }}>{bot.transfers_today || 0}</td>
                                    <td className="bot-disp" style={{ color: '#6b7280' }}>{bot.am_today || '-'}</td>
                                    <td className="bot-disp" style={{ color: '#f59e0b' }}>{bot.ni_today || '-'}</td>
                                    <td className="bot-disp" style={{ color: '#ef4444' }}>{bot.dnc_today || '-'}</td>
                                    <td className="bot-heartbeat">{timeAgo(bot.last_heartbeat)}</td>
                                    <td>
                                        <div className="bot-actions">
                                            {/* Bot Actions */}
                                            {bot.effectiveStatus === 'offline' || bot.effectiveStatus === 'waiting_approval' ? (
                                                <button
                                                    className={`action-btn start ${isLoading === 'start' ? 'loading' : ''}`}
                                                    onClick={() => onAction(bot.sip_id, 'start', bot)}
                                                    disabled={isLoading}
                                                    title="Start Bot"
                                                >
                                                    <Play size={12} />
                                                </button>
                                            ) : (
                                                <button
                                                    className={`action-btn stop ${isLoading === 'stop' ? 'loading' : ''}`}
                                                    onClick={() => onAction(bot.sip_id, 'stop', bot)}
                                                    disabled={isLoading}
                                                    title="Stop Bot"
                                                >
                                                    <Square size={12} />
                                                </button>
                                            )}

                                            {/* VM Actions - Separate with visual distinction */}
                                            {bot.vmid && (
                                                isVmConfirming ? (
                                                    <div className="vm-confirm-group">
                                                        <button
                                                            className="action-btn vm-confirm"
                                                            onClick={() => onVMRestart(bot)}
                                                            disabled={isLoading === 'vm_restart'}
                                                            title="Confirm VM Restart"
                                                        >
                                                            <Check size={12} />
                                                        </button>
                                                        <button
                                                            className="action-btn vm-cancel"
                                                            onClick={() => setVmConfirm(null)}
                                                            title="Cancel"
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className={`action-btn vm-action ${isLoading === 'vm_restart' ? 'loading' : ''}`}
                                                        onClick={() => setVmConfirm(bot.sip_id)}
                                                        disabled={isLoading}
                                                        title="Restart VM (Requires Confirmation)"
                                                    >
                                                        <Power size={12} />
                                                    </button>
                                                )
                                            )}

                                            <button
                                                className="exclude-btn"
                                                onClick={() => onExclude(bot.sip_id)}
                                                title="Exclude from monitoring"
                                            >
                                                <EyeOff size={12} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <span className="pagination-info">
                    Showing {((currentPage - 1) * 50) + 1}-{Math.min(currentPage * 50, filteredCount)} of {filteredCount}
                    {excludedCount > 0 && ` (${excludedCount} excluded)`}
                </span>
                <div className="pagination-controls">
                    <button
                        className="page-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                    >
                        Prev
                    </button>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        Page {currentPage} of {totalPages || 1}
                    </span>
                    <button
                        className="page-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage >= totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>
        </>
    )
}

// ============================================
// BY DIALER TAB
// ============================================
function ByDialerTab({ stats }) {
    if (!stats || stats.length === 0) {
        return (
            <div className="empty-state">
                <Phone size={48} />
                <p>No dialer statistics available</p>
            </div>
        )
    }

    return (
        <div className="performance-table-container">
            <table className="performance-table">
                <thead>
                    <tr>
                        <th>Dialer</th>
                        <th style={{ textAlign: 'right' }}>Bots</th>
                        <th style={{ textAlign: 'right' }}>Calls</th>
                        <th style={{ textAlign: 'right' }}>Xfers</th>
                        <th style={{ textAlign: 'right' }}>Rate</th>
                        <th style={{ textAlign: 'right' }}>Avg Dur</th>
                    </tr>
                </thead>
                <tbody>
                    {stats.map((row, i) => {
                        const rate = calcRate(row.total_calls, row.transfers)
                        const rateClass = parseFloat(rate) >= 5 ? 'good' : parseFloat(rate) >= 3 ? 'ok' : 'bad'

                        return (
                            <tr key={i}>
                                <td>
                                    <Phone size={14} style={{ marginRight: 8, opacity: 0.5 }} />
                                    {row.dialer_name || `Dialer ${row.dialer_id}`}
                                </td>
                                <td style={{ textAlign: 'right' }}>{row.active_bots || 0}</td>
                                <td style={{ textAlign: 'right' }}>{formatNumber(row.total_calls)}</td>
                                <td style={{ textAlign: 'right', color: '#10b981' }}>{formatNumber(row.transfers)}</td>
                                <td style={{ textAlign: 'right' }}>
                                    <div className="rate-bar">
                                        <div className="rate-bar-track">
                                            <div
                                                className={`rate-bar-fill ${rateClass}`}
                                                style={{ width: `${Math.min(parseFloat(rate) * 10, 100)}%` }}
                                            ></div>
                                        </div>
                                        <span>{rate}%</span>
                                    </div>
                                </td>
                                <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                                    {row.avg_duration ? `${Math.round(row.avg_duration)}s` : '-'}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

// ============================================
// BY VOICE SET TAB
// ============================================
function ByVoiceSetTab({ stats }) {
    if (!stats || stats.length === 0) {
        return (
            <div className="empty-state">
                <Mic size={48} />
                <p>No voice set statistics available</p>
            </div>
        )
    }

    return (
        <div className="performance-table-container">
            <table className="performance-table">
                <thead>
                    <tr>
                        <th>Voice Set</th>
                        <th style={{ textAlign: 'right' }}>Avatars</th>
                        <th style={{ textAlign: 'right' }}>Calls</th>
                        <th style={{ textAlign: 'right' }}>Xfers</th>
                        <th style={{ textAlign: 'right' }}>Rate</th>
                        <th style={{ textAlign: 'right' }}>Avg Dur</th>
                    </tr>
                </thead>
                <tbody>
                    {stats.map((row, i) => {
                        const rate = calcRate(row.total_calls, row.transfers)
                        const rateClass = parseFloat(rate) >= 5 ? 'good' : parseFloat(rate) >= 3 ? 'ok' : 'bad'

                        return (
                            <tr key={i}>
                                <td>
                                    <Mic size={14} style={{ marginRight: 8, opacity: 0.5 }} />
                                    {row.voice_set_name || 'Classic Mode'}
                                </td>
                                <td style={{ textAlign: 'right' }}>{row.avatars_using || 0}</td>
                                <td style={{ textAlign: 'right' }}>{formatNumber(row.total_calls)}</td>
                                <td style={{ textAlign: 'right', color: '#10b981' }}>{formatNumber(row.transfers)}</td>
                                <td style={{ textAlign: 'right' }}>
                                    <div className="rate-bar">
                                        <div className="rate-bar-track">
                                            <div
                                                className={`rate-bar-fill ${rateClass}`}
                                                style={{ width: `${Math.min(parseFloat(rate) * 10, 100)}%` }}
                                            ></div>
                                        </div>
                                        <span>{rate}%</span>
                                    </div>
                                </td>
                                <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                                    {row.avg_duration ? `${Math.round(row.avg_duration)}s` : '-'}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

// ============================================
// BY AVATAR TAB
// ============================================
function ByAvatarTab({ stats }) {
    if (!stats || stats.length === 0) {
        return (
            <div className="empty-state">
                <Users size={48} />
                <p>No avatar statistics available</p>
            </div>
        )
    }

    return (
        <div className="performance-table-container">
            <table className="performance-table">
                <thead>
                    <tr>
                        <th>Avatar</th>
                        <th>Dialer</th>
                        <th>Voice Set</th>
                        <th style={{ textAlign: 'right' }}>Bots</th>
                        <th style={{ textAlign: 'right' }}>Calls</th>
                        <th style={{ textAlign: 'right' }}>Xfers</th>
                        <th style={{ textAlign: 'right' }}>Rate</th>
                    </tr>
                </thead>
                <tbody>
                    {stats.map((row, i) => {
                        const rate = calcRate(row.total_calls, row.transfers)
                        const rateClass = parseFloat(rate) >= 5 ? 'good' : parseFloat(rate) >= 3 ? 'ok' : 'bad'

                        return (
                            <tr key={i}>
                                <td>
                                    <Users size={14} style={{ marginRight: 8, opacity: 0.5 }} />
                                    {row.avatar_name || `Avatar ${row.avatar_id}`}
                                </td>
                                <td style={{ color: 'var(--text-muted)' }}>{row.dialer_name || '-'}</td>
                                <td style={{ color: 'var(--text-muted)' }}>{row.voice_set_name || 'Classic'}</td>
                                <td style={{ textAlign: 'right' }}>{row.active_bots || 0}</td>
                                <td style={{ textAlign: 'right' }}>{formatNumber(row.total_calls)}</td>
                                <td style={{ textAlign: 'right', color: '#10b981' }}>{formatNumber(row.transfers)}</td>
                                <td style={{ textAlign: 'right' }}>
                                    <div className="rate-bar">
                                        <div className="rate-bar-track">
                                            <div
                                                className={`rate-bar-fill ${rateClass}`}
                                                style={{ width: `${Math.min(parseFloat(rate) * 10, 100)}%` }}
                                            ></div>
                                        </div>
                                        <span>{rate}%</span>
                                    </div>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

// ============================================
// ERRORS TAB
// ============================================
function ErrorsTab({ errors, expandedError, setExpandedError, onRefresh }) {
    const unresolvedErrors = errors.filter(e => !e.resolved)
    const [filter, setFilter] = useState('unresolved')

    const filteredErrors = filter === 'unresolved'
        ? unresolvedErrors
        : filter === 'all'
            ? errors
            : errors.filter(e => e.error_type === filter)

    // Get unique error types
    const errorTypes = [...new Set(errors.map(e => e.error_type))]

    const handleResolve = async (errorId) => {
        try {
            await monitoringApi.resolveError(errorId)
            onRefresh()
        } catch (err) {
            console.error('Failed to resolve error:', err)
        }
    }

    return (
        <div className="errors-tab">
            <div className="errors-header">
                <div className="filter-pills">
                    <button
                        className={`filter-pill ${filter === 'unresolved' ? 'active' : ''}`}
                        onClick={() => setFilter('unresolved')}
                    >
                        Unresolved ({unresolvedErrors.length})
                    </button>
                    <button
                        className={`filter-pill ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All ({errors.length})
                    </button>
                    {errorTypes.slice(0, 4).map(type => (
                        <button
                            key={type}
                            className={`filter-pill ${filter === type ? 'active' : ''}`}
                            onClick={() => setFilter(type)}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {filteredErrors.length === 0 ? (
                <div className="empty-state">
                    <CheckCircle size={48} />
                    <p>No errors to display</p>
                </div>
            ) : (
                <div className="errors-list">
                    {filteredErrors.map(err => (
                        <div
                            key={err.id}
                            className={`error-item ${expandedError === err.id ? 'expanded' : ''} ${err.resolved ? 'resolved' : ''}`}
                        >
                            <div
                                className="error-item-header"
                                onClick={() => setExpandedError(expandedError === err.id ? null : err.id)}
                            >
                                <div className="error-item-main">
                                    <span className={`error-type-badge ${err.error_type}`}>
                                        {err.error_type}
                                    </span>
                                    <span className="error-sip">{err.sip_id}</span>
                                    <span className="error-message-short">
                                        {err.error_message?.substring(0, 60)}
                                        {err.error_message?.length > 60 ? '...' : ''}
                                    </span>
                                </div>
                                <div className="error-item-meta">
                                    <span className="error-time">{timeAgo(err.created_at)}</span>
                                    <ChevronRight
                                        size={16}
                                        className={`expand-icon ${expandedError === err.id ? 'expanded' : ''}`}
                                    />
                                </div>
                            </div>

                            {expandedError === err.id && (
                                <div className="error-item-details">
                                    <div className="error-detail-row">
                                        <label>Full Message:</label>
                                        <span>{err.error_message}</span>
                                    </div>
                                    {err.error_stack && (
                                        <div className="error-detail-row">
                                            <label>Stack Trace:</label>
                                            <pre className="error-stack">{err.error_stack}</pre>
                                        </div>
                                    )}
                                    <div className="error-detail-row">
                                        <label>Bot Version:</label>
                                        <span>{err.bot_version || 'Unknown'}</span>
                                    </div>
                                    <div className="error-detail-row">
                                        <label>Time:</label>
                                        <span>{new Date(err.created_at).toLocaleString()}</span>
                                    </div>
                                    {!err.resolved && (
                                        <div className="error-actions">
                                            <button
                                                className="action-btn"
                                                onClick={() => handleResolve(err.id)}
                                            >
                                                <Check size={12} /> Mark Resolved
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// ============================================
// ISSUES TAB
// ============================================
function IssuesTab({ bots, excludedBots, onApprove, onBulkApprove }) {
    const waitingBots = bots.filter(b => b.effectiveStatus === 'waiting_approval' && !excludedBots.includes(b.sip_id))
    const errorBots = bots.filter(b => b.effectiveStatus === 'error' && !excludedBots.includes(b.sip_id))
    const offlineBots = bots.filter(b => {
        if (excludedBots.includes(b.sip_id)) return false
        if (b.effectiveStatus !== 'offline') return false
        if (!b.last_heartbeat) return true
        const hoursOffline = (new Date() - new Date(b.last_heartbeat)) / (1000 * 60 * 60)
        return hoursOffline > 1
    })

    // Group by dialer
    const groupByDialer = (botList) => {
        const groups = {}
        botList.forEach(bot => {
            const dialerName = bot.dialer_name || 'Unknown'
            if (!groups[dialerName]) groups[dialerName] = []
            groups[dialerName].push(bot)
        })
        return groups
    }

    return (
        <div className="issues-sections">
            {/* Waiting for Approval */}
            {waitingBots.length > 0 && (
                <div className="issues-section">
                    <div className="issues-section-header">
                        <span className="issues-section-title waiting">
                            <Clock size={16} />
                            Waiting for Approval
                            <span className="issues-section-count">{waitingBots.length}</span>
                        </span>
                        <button className="alert-action primary" onClick={onBulkApprove}>
                            Approve All
                        </button>
                    </div>
                    <div className="issues-section-body">
                        {Object.entries(groupByDialer(waitingBots)).map(([dialer, dialerBots]) => (
                            <div key={dialer} style={{ marginBottom: 'var(--space-sm)' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                                    {dialer}
                                </div>
                                <div className="issue-bots-list">
                                    {dialerBots.map(bot => (
                                        <div key={bot.sip_id} className="issue-bot-chip" onClick={() => onApprove(bot.sip_id, 'start', bot)}>
                                            {bot.sip_id}
                                            <Check size={12} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Errors */}
            {errorBots.length > 0 && (
                <div className="issues-section">
                    <div className="issues-section-header">
                        <span className="issues-section-title error">
                            <AlertCircle size={16} />
                            Errors
                            <span className="issues-section-count">{errorBots.length}</span>
                        </span>
                    </div>
                    <div className="issues-section-body">
                        {Object.entries(groupByDialer(errorBots)).map(([dialer, dialerBots]) => (
                            <div key={dialer} style={{ marginBottom: 'var(--space-sm)' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                                    {dialer}
                                </div>
                                <div className="issue-bots-list">
                                    {dialerBots.map(bot => (
                                        <div key={bot.sip_id} className="issue-bot-chip">
                                            {bot.sip_id}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Offline > 1 Hour */}
            {offlineBots.length > 0 && (
                <div className="issues-section">
                    <div className="issues-section-header">
                        <span className="issues-section-title offline">
                            <Activity size={16} />
                            Offline &gt; 1 Hour
                            <span className="issues-section-count">{offlineBots.length}</span>
                        </span>
                    </div>
                    <div className="issues-section-body">
                        {Object.entries(groupByDialer(offlineBots)).map(([dialer, dialerBots]) => (
                            <div key={dialer} style={{ marginBottom: 'var(--space-sm)' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                                    {dialer}
                                </div>
                                <div className="issue-bots-list">
                                    {dialerBots.slice(0, 20).map(bot => (
                                        <div key={bot.sip_id} className="issue-bot-chip">
                                            {bot.sip_id}
                                            <span className="chip-dialer">{timeAgo(bot.last_heartbeat)}</span>
                                        </div>
                                    ))}
                                    {dialerBots.length > 20 && (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                            +{dialerBots.length - 20} more
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {waitingBots.length === 0 && errorBots.length === 0 && offlineBots.length === 0 && (
                <div className="empty-state">
                    <CheckCircle size={48} />
                    <p>No issues found - all bots are healthy!</p>
                </div>
            )}
        </div>
    )
}
